import { remissionGuideApi } from "./axios";
import { useQuery } from "@tanstack/react-query";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";
import {
  ServiceResponseI,
  DataI,
  GetRemissionGuideParamsI,
  ListRemissionGuideI,
  OneRemissionGuideForFrontI,
  NewRemissionGuideI,
} from "../../interfaces";
import { AxiosError } from "axios";

const getRemissionGuides = async ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  dateFrom,
  dateTo,
}: GetRemissionGuideParamsI): Promise<ServiceResponseI<DataI<ListRemissionGuideI>>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();
    params.append("page",    (pagination.pageIndex + 1).toString());
    params.append("limit",   pagination.pageSize.toString());
    params.append("search",  globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    if (branchId) params.append("branchId", branchId);
    if (dateFrom) params.append("dateFrom", dateFrom);
    if (dateTo)   params.append("dateTo",   dateTo);

    const { data } = await remissionGuideApi.get<ServiceResponseI<DataI<ListRemissionGuideI>>>("", {
      params,
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const remissionGuideById = async (
  id: string,
): Promise<ServiceResponseI<OneRemissionGuideForFrontI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await remissionGuideApi.get<ServiceResponseI<OneRemissionGuideForFrontI>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const remissionGuidePost = async (
  payload: NewRemissionGuideI,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await remissionGuideApi.post<ServiceResponseI<string>>(
      "",
      payload,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const remissionGuideAnnul = async (
  id: string,
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await remissionGuideApi.delete<ServiceResponseI<string>>(
      `/${id}`,
      { headers: { Authorization: validateToken } },
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) LogoutSystem();
    throw new Error((obj?.data as Error).message);
  }
};

export const useRemissionGuides = ({
  pagination,
  sorting,
  globalFilter,
  branchId,
  dateFrom,
  dateTo,
}: GetRemissionGuideParamsI) => {
  const remissionGuidesQuery = useQuery(
    ["remission-guides", { pagination, sorting, globalFilter, branchId, dateFrom, dateTo }],
    () => getRemissionGuides({ pagination, sorting, globalFilter, branchId, dateFrom, dateTo }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData:     true,
    },
  );

  return { remissionGuidesQuery };
};
